import { Box } from '@mui/material';
import { isValidLandingPageUrl } from '@provision/landing-page-utils';
import { useEffect, useRef, useState } from 'react';
import { COPY } from '../copy';
import { imageFitSx } from '../image-fit';
import { PageSurface } from '../layouts/block-surface';
import { useLayoutStyles } from '../layouts/layout-context';
import type { Sx } from '../layouts/types';
import { blockImageUrl, type MediaBlock } from '../media';
import { resolveVideoEmbed, type VideoEmbed } from '../video-embed';
import { PlayIcon } from './icons';

type Video = NonNullable<MediaBlock['video']>;

const VideoPlayer = ({ embed, title }: { embed: VideoEmbed; title?: string }) => {
  const s = useLayoutStyles('media');
  const ref = useRef<HTMLElement>(null);

  // The poster button the visitor clicked is gone; keep their focus on what replaced it.
  useEffect(() => ref.current?.focus(), []);

  if (embed.kind === 'file') {
    return (
      <Box
        component="video"
        ref={ref}
        src={embed.src}
        controls
        autoPlay
        playsInline
        aria-label={title || COPY.video}
        sx={s.player}
      />
    );
  }

  return (
    <Box
      component="iframe"
      ref={ref}
      src={embed.src}
      title={title || COPY.video}
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
      sx={s.player}
    />
  );
};

// The poster stays until the visitor clicks, so no third-party player loads with the page.
const VideoMedia = ({ video, frameSx }: { video: Video | undefined; frameSx: Sx }) => {
  const s = useLayoutStyles('media');
  const [playing, setPlaying] = useState(false);

  const url = video?.mediaUrl?.trim();
  const embed = url ? resolveVideoEmbed(url) : null;

  const poster = (
    <>
      <Box sx={s.scene} />
      <Box sx={s.play}>
        <PlayIcon />
      </Box>
      {video?.title || video?.caption ? (
        <Box sx={s.meta}>
          <div>
            {video.title ? <Box sx={s.title}>{video.title}</Box> : null}
            {video.caption ? <Box sx={s.caption}>{video.caption}</Box> : null}
          </div>
        </Box>
      ) : null}
    </>
  );

  let content = <Box sx={s.poster}>{poster}</Box>;
  if (embed && playing) {
    content = <VideoPlayer embed={embed} title={video?.title} />;
  } else if (embed) {
    content = (
      <Box
        component="button"
        type="button"
        aria-label={COPY.playVideo}
        onClick={() => setPlaying(true)}
        sx={s.poster}
      >
        {poster}
      </Box>
    );
  } else if (url && isValidLandingPageUrl(url)) {
    content = (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={COPY.playVideo}
        sx={s.poster}
      >
        {poster}
      </Box>
    );
  }

  return <Box sx={[frameSx, s.card]}>{content}</Box>;
};

/** A hero or content block's image or video, filling the frame the block lays out for it. */
export const BlockMedia = ({ block, frameSx }: { block: MediaBlock; frameSx: Sx }) => {
  const s = useLayoutStyles('media');

  // The card keeps the page's look on any block background; keyed by URL so an edited link in
  // the editor preview goes back to its poster.
  if (block.mediaKind === 'video') {
    return (
      <PageSurface>
        <VideoMedia key={block.video?.mediaUrl} video={block.video} frameSx={frameSx} />
      </PageSurface>
    );
  }

  const imageUrl = block.mediaKind === 'image' ? blockImageUrl(block) : undefined;
  if (!imageUrl) return null;

  return (
    <Box sx={frameSx}>
      <Box
        component="img"
        src={imageUrl}
        alt=""
        sx={[s.image, imageFitSx(block.imageFit, block.imageFocusX, block.imageFocusY)]}
      />
    </Box>
  );
};
